import { useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, TextField, Tooltip } from "@mui/material";
import { WhatsApp } from "@mui/icons-material";
import MDBox from "../../controls/MDBox";
import MDButton from "../../controls/MDButton";
import MDTypography from "../../controls/MDTypography";
import { fetchMensajes, armarMensaje } from "../../Utils/Mensaje";

// Código de país que se antepone a los teléfonos cargados sin él (Argentina).
const CODIGO_PAIS = "54";

// Deja solo dígitos y garantiza el código de país, que es lo que pide wa.me.
const normalizarTelefono = (valor) => {
  let digitos = (valor || "").replace(/\D/g, "").replace(/^00/, "");
  if (!digitos) return "";
  if (digitos.startsWith(CODIGO_PAIS)) return digitos;
  return CODIGO_PAIS + digitos.replace(/^0/, "");
};

// Mismo criterio que el listado de contactos: primero los que tienen prioridad (orden >= 0)
// de menor a mayor, y al final los que no tienen.
const porPrioridad = (a, b) => {
  const ordenA = a.orden ?? -1;
  const ordenB = b.orden ?? -1;
  if (ordenA >= 0 && ordenB >= 0) return ordenA - ordenB;
  if (ordenA >= 0) return -1;
  if (ordenB >= 0) return 1;
  return 0;
};

const colorPrioridad = (nombre) =>
  !nombre ? "#757575" : nombre.toLowerCase() === "prioritario" ? "#1976d2" : "#d32f2f";

const textoTelefono = (c) => `${c.tipoContacto ? `${c.tipoContacto}: ` : ""}${c.contacto}`;

const esTelefonico = (contacto) => {
  const tipo = (contacto.tipoContacto || "").toLowerCase();
  if (tipo.includes("mail")) return false;
  return normalizarTelefono(contacto.contacto).length >= 8;
};

// Botón verde de WhatsApp para cada caso: abre un diálogo donde se elige el teléfono
// del sujeto y el Tipo Mensaje, y se abre WhatsApp con el texto armado.
const WhatsAppCaso = ({ item }) => {
  const [open, setOpen] = useState(false);
  const [mensajes, setMensajes] = useState([]);
  const [idContacto, setIdContacto] = useState("");
  const [idMensaje, setIdMensaje] = useState("");
  const [texto, setTexto] = useState("");

  const telefonos = (item.sujetoContactos || []).filter(esTelefonico).sort(porPrioridad);

  const datosCaso = {
    contribuyente: item.nombreSujeto,
    gestor: item.usuario,
    organismo: item.organismo,
    objetos: item.objetos ? item.objetos.replace(/,(?=\S)/g, ", ") : "",
  };

  const handleOpen = async (e) => {
    e.stopPropagation();
    setOpen(true);
    setTexto("");
    setIdMensaje("");
    setIdContacto(telefonos.length === 1 ? telefonos[0].idSujetoContacto : "");
    setMensajes(await fetchMensajes());
  };

  const handleElegirMensaje = (id) => {
    setIdMensaje(id);
    const elegido = mensajes.find((m) => m.idMensaje === id);
    setTexto(armarMensaje(elegido?.texto, datosCaso));
  };

  const handleEnviar = () => {
    const contacto = telefonos.find((c) => c.idSujetoContacto === idContacto);
    const url = `https://wa.me/${normalizarTelefono(contacto.contacto)}?text=${encodeURIComponent(texto)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const sinTelefonos = telefonos.length === 0;

  return (
    <>
      <Tooltip title={sinTelefonos ? "El sujeto no tiene teléfonos cargados" : "Enviar mensaje por WhatsApp"}>
        <span>
          <MDBox
            display="flex"
            alignItems="center"
            sx={{
              cursor: sinTelefonos ? "not-allowed" : "pointer",
              padding: "4px 6px",
              borderRadius: 1,
              opacity: sinTelefonos ? 0.4 : 1,
              backgroundColor: "rgba(37, 211, 102, 0.12)",
              "&:hover": { backgroundColor: sinTelefonos ? undefined : "rgba(37, 211, 102, 0.25)" },
            }}
            onClick={sinTelefonos ? (e) => e.stopPropagation() : handleOpen}
          >
            <WhatsApp sx={{ fontSize: 18, color: "#25D366" }} />
          </MDBox>
        </span>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth onClick={(e) => e.stopPropagation()}>
        <DialogTitle>Enviar WhatsApp a {item.nombreSujeto}</DialogTitle>
        <DialogContent>
          <MDBox pt={1} display="flex" flexDirection="column" gap={2}>
            <TextField
              select
              label="Teléfono"
              value={idContacto}
              onChange={(e) => setIdContacto(e.target.value)}
              fullWidth
              InputProps={{ sx: { height: 44 } }}
              SelectProps={{
                // La caja cerrada muestra todo en una sola línea (con "…" si no entra).
                renderValue: (id) => {
                  const c = telefonos.find((t) => t.idSujetoContacto === id);
                  if (!c) return "";
                  return (
                    <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {textoTelefono(c)}
                      <span style={{ marginLeft: 8, fontWeight: 600, color: colorPrioridad(c.prioridadNombre) }}>
                        {`- ${c.prioridadNombre || "Sin prioridad"}`}
                      </span>
                      <span style={{ marginLeft: 8, color: "#616161" }}>{`(Origen: ${c.origen || "sin origen"})`}</span>
                    </span>
                  );
                },
              }}
            >
              {telefonos.map((c) => (
                <MenuItem key={c.idSujetoContacto} value={c.idSujetoContacto}>
                  <div>
                    <div>{textoTelefono(c)}</div>
                    <div style={{ fontSize: 12 }}>
                      <span style={{ fontWeight: 600, color: colorPrioridad(c.prioridadNombre) }}>
                        {c.prioridadNombre || "Sin prioridad"}
                      </span>
                      <span style={{ color: "#616161" }}>{` · Origen: ${c.origen || "sin origen"}`}</span>
                    </div>
                  </div>
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Tipo Mensaje"
              value={idMensaje}
              onChange={(e) => handleElegirMensaje(e.target.value)}
              helperText={mensajes.length === 0 ? "No hay mensajes cargados (Configuraciones → Mensajes)" : ""}
              fullWidth
              InputProps={{ sx: { height: 44 } }}
            >
              {mensajes.map((m) => (
                <MenuItem key={m.idMensaje} value={m.idMensaje}>
                  {m.tipoMensaje}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Mensaje (se puede editar antes de enviar)"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              multiline
              minRows={5}
              fullWidth
            />
            <MDTypography variant="caption" color="text">
              Se abrirá WhatsApp con este texto; el envío lo confirma el usuario desde WhatsApp.
            </MDTypography>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="dark" onClick={() => setOpen(false)}>
            Cancelar
          </MDButton>
          <MDButton
            variant="gradient"
            color="success"
            startIcon={<WhatsApp />}
            disabled={!idContacto || !texto.trim()}
            onClick={handleEnviar}
          >
            Abrir WhatsApp
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WhatsAppCaso;
